"use client";
import featureIcon_4 from "@/assets/images/icon/Event2.svg";
import { correctUrlString, groupkebabCase } from "@/utils/utils";
import Image from "next/image";
import Link from "next/link";
import HandleCart from "@/components/common/HandleCart";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { GraduationCap, Cake, Users, Tag } from "lucide-react";
import type { ReactNode } from "react";
import EventCard, {
  EventGroupAggregate,
} from "@/components/viva-vibgyor/event-card";
import { useDispatch, useSelector } from "react-redux";
import {
  removeVivaItem,
  selectVivaCartItems,
  VivaCartItem,
} from "@/redux/features/vivaCartSlice";

const formatPrice = (v: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(v);

const formatGrade = (g: string) => (/grade/i.test(g) ? g : `Grade ${g}`);

const PropertyOne = ({ events }: any) => {
  const router = useRouter();
  const params = new URLSearchParams();
  const cart = useSelector(selectVivaCartItems);
  const userRoleVal = useSelector((state: any) => state.auth.userRole);
  const dispatch = useDispatch();

  const [aggregates, setAggregates] = useState<
    Record<string, EventGroupAggregate>
  >({});
  const [aggLoading, setAggLoading] = useState(true);
  const normalizeGroupKey = (name: string) => (name || "").trim().toLowerCase();
  const handleRemoveFromCart = (data: VivaCartItem) => {
    dispatch(
      removeVivaItem({
        data,
        persist: false,
        strictRemove: false,
      }),
    );
  };

  useEffect(() => {
    if (!events || events.length === 0) {
      setAggLoading(false);
      return;
    }
    const group_names = events.map((e: any) => e.group_name).filter(Boolean);
    if (!group_names.length) {
      setAggLoading(false);
      return;
    }

    fetch("/api/events/v3/group-aggregates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ group_names, registrationsOpen: true }),
    })
      .then((r) => r.json())
      .then((data: any) => {
        if (data?.aggregates) {
          setAggregates((prev) => ({ ...prev, ...data.aggregates }));
        }
      })
      .catch(() => {})
      .finally(() => setAggLoading(false));
  }, [events]);

  if (!events || events.length === 0) return null;

  return (
    <>
      <style>{`
        .upcoming-section {
          background: #fff;
          padding: 3.5rem 0;
        }
        .upcoming-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        .upcoming-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .upcoming-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
        }
        .uc-event-card {
          background: #fff;
          border-radius: 0.875rem;
          overflow: hidden;
          border: 1px solid #eef0f8;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          display: flex;
          flex-direction: column;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .uc-event-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 28px rgba(63,65,209,0.12);
        }
        .uc-img-wrap {
          position: relative;
          width: 100%;
          padding-bottom: 60%;
          overflow: hidden;
          background: #eef0ff;
        }
        .uc-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #fff;
          color: #3f41d1;
          border: 1px solid #d0d3f5;
          border-radius: 2rem;
          font-size: 0.72rem;
          font-weight: 600;
          padding: 3px 10px;
          z-index: 1;
          letter-spacing: 0.02em;
        }
        .uc-body {
          padding: 0.875rem 1rem 0;
          flex: 1;
        }
        .uc-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1a1a1a;
          font-family: Graphik, sans-serif;
          line-height: 1.4;
          text-decoration: none;
          display: block;
          margin-bottom: 0.5rem;
        }
        .uc-title:hover { color: #3f41d1; }
        .uc-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.35rem;
          margin-bottom: 0.5rem;
        }
        .uc-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          background: #f0f2ff;
          color: #3f41d1;
          border-radius: 2rem;
          font-size: 0.7rem;
          font-weight: 500;
          padding: 3px 8px;
          white-space: nowrap;
        }
        .uc-chip-skel {
          width: 68px;
          height: 22px;
          background: linear-gradient(90deg, #f0f2ff 25%, #e0e3ff 50%, #f0f2ff 75%);
          background-size: 200% 100%;
          animation: uc-shimmer 1.2s infinite;
          border-radius: 2rem;
          display: inline-block;
        }
        @keyframes uc-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .uc-price {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.82rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 0.25rem;
        }
        .uc-footer {
          padding: 0.75rem 1rem 1rem;
          margin-top: auto;
        }
        .uc-view-all {
          font-size: 0.875rem;
          font-weight: 500;
          color: #3f41d1;
          background: #eef0ff;
          border: none;
          border-radius: 2rem;
          padding: 0.5rem 1.25rem;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .uc-view-all:hover { background: #3f41d1; color: #fff; }
        @media (max-width: 900px) {
          .upcoming-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 560px) {
          .upcoming-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <section className="upcoming-section">
        <div className="upcoming-container">
          <div className="upcoming-header">
            <div>
              <div
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#3f41d1",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  marginBottom: "0.35rem",
                }}
              >
                Don&apos;t Miss Out
              </div>
              <h2
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                  margin: 0,
                  fontFamily: "Graphik, sans-serif",
                }}
              >
                What&apos;s Coming Up Next
              </h2>
              <p
                style={{
                  color: "#666",
                  fontSize: "0.9rem",
                  margin: "0.25rem 0 0",
                  fontFamily: "Graphik, sans-serif",
                }}
              >
                Competitions, workshops &amp; premium events open for
                registration.
              </p>
            </div>
            <button
              className="uc-view-all"
              onClick={() => router.push("/events")}
            >
              View All →
            </button>
          </div>

          <div className="upcoming-grid">
            {events.map((item: any) => {
              const pathname = `/event/${groupkebabCase(item?.participation_type)}/${groupkebabCase(item?.["Sub Type"])}/${groupkebabCase(item?.Category)}/${groupkebabCase(item?.group_name)}`;
              const detailHref = params.toString()
                ? `${pathname}?${params.toString()}`
                : pathname;
              const key = normalizeGroupKey(
                [
                  item?.group_name,
                  item?.participation_type,
                  item?.Category,
                ].join("|") || "",
              );
              const aggregate = aggregates[key] || null;

              return (
                <EventCard
                  key={item?.id ?? detailHref}
                  item={item}
                  pathname={detailHref}
                  aggregate={aggregate}
                  aggregateLoading={aggLoading}
                  userRole={userRoleVal}
                  cart={cart}
                  paginatedEvents={events}
                  onRegister={() => {
                    if (item.inCart > 0) {
                      router.push("/cart");
                    } else {
                      router.push(detailHref);
                    }
                  }}
                  // onBulkUpload={() => {
                  //   handleBulkUpload(item);
                  // }}
                  onAddToCart={() => {
                    router.push(detailHref);
                  }}
                  onRemoveFromCart={async () => {
                    handleRemoveFromCart(item);
                  }}
                />
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default PropertyOne;
